/**
 * Question Transformation Functions
 * Convert between single/multiple choice, normalize questions, sanitize options
 */

import { Logger } from '../logger';

const log = Logger.create('questionTransformers');

/**
 * Convert a question to single choice
 */
export function convertToSingleChoice(question) {
  return {
    ...question,
    allowMultiple: false,
    correctAnswerIndex: question.correctAnswerIndices?.[0] || question.correctAnswerIndex || 0,
    correctAnswerIndices: undefined
  };
}

/**
 * Convert a question to multiple choice
 */
export function convertToMultipleChoice(question) {
  return {
    ...question,
    allowMultiple: true,
    correctAnswerIndices: [question.correctAnswerIndex || 0],
    correctAnswerIndex: question.correctAnswerIndex || 0
  };
}

/**
 * Toggle between single and multiple choice
 */
export function toggleMultipleChoice(question) {
  if (question.allowMultiple) {
    return convertToSingleChoice(question);
  } else {
    return convertToMultipleChoice(question);
  }
}

/**
 * Normalize a question object (ensure all required fields exist)
 * 
 * INPUT: Accepts multiple legacy schemas for backward compatibility:
 * - correctAnswerIndices (current standard)
 * - correctAnswers (old Template Editor)
 * - correctAnswerIndex (legacy single choice)
 * - correctAnswer (very old legacy)
 * 
 * OUTPUT: ONLY uses correctAnswerIndices (array of numbers) - the single standard!
 */
export function normalizeQuestion(question) {
  // Determine correct answer handling
  let correctAnswerIndices = [];

  // Helper to ensure number conversion
  const toNumber = (val) => {
    const num = Number(val);
    return isNaN(num) ? null : num;
  };

  // PRIORITY 1: correctAnswerIndices (current standard)
  if (question.correctAnswerIndices && Array.isArray(question.correctAnswerIndices) && question.correctAnswerIndices.length > 0) {
    correctAnswerIndices = question.correctAnswerIndices.map(toNumber).filter(n => n !== null);
  }
  // PRIORITY 2: correctAnswers (old Template Editor format)
  else if (question.correctAnswers && Array.isArray(question.correctAnswers) && question.correctAnswers.length > 0) {
    correctAnswerIndices = question.correctAnswers.map(toNumber).filter(n => n !== null);
  }
  // PRIORITY 3: correctAnswerIndex (legacy single choice)
  else if (question.correctAnswerIndex !== undefined && question.correctAnswerIndex !== null) {
    const idx = toNumber(question.correctAnswerIndex);
    if (idx !== null) {
      correctAnswerIndices = [idx];
    }
  }
  // PRIORITY 4: correctAnswer (very old legacy)
  else if (question.correctAnswer !== undefined && question.correctAnswer !== null) {
    const idx = toNumber(question.correctAnswer);
    if (idx !== null) {
      correctAnswerIndices = [idx];
    }
  }

  // Default to first option if nothing valid found
  if (correctAnswerIndices.length === 0) {
    correctAnswerIndices = [0];
  }

  // Determine if multiple choice based on array length or explicit flag
  const allowMultiple = question.allowMultiple || correctAnswerIndices.length > 1;

  // OUTPUT: Single standard format - only correctAnswerIndices!
  return {
    question: question.question || '',
    codeBlock: {
      language: question.codeBlock?.language || '',
      code: question.codeBlock?.code || ''
    },
    terminalOutput: question.terminalOutput || '',
    options: question.options || [],
    correctAnswerIndices: correctAnswerIndices, // THE ONLY correct answer field!
    allowMultiple: allowMultiple,
    explanation: question.explanation || ''
  };
}

/**
 * Sanitize options (trim whitespace, remove empty options)
 */
export function sanitizeOptions(options) {
  return options
    .map(opt => opt?.trim())
    .filter(opt => opt && opt.length > 0);
}

/**
 * Parse LLM response and extract JSON
 * Automatically normalizes questions to internal format
 */
export function parseQuestionResponse(response) {
  let parsed;
  
  // Debug: Log raw response
  log.debug('=== LLM RAW RESPONSE ===');
  log.debug(response);
  log.debug('=== END RAW RESPONSE ===');
  
  // Find ALL code blocks (handles nested blocks by finding all matches)
  const allCodeBlocks = [...response.matchAll(/```(\w*)\s*([\s\S]*?)```/g)];
  log.debug(`Found ${allCodeBlocks.length} code block(s)`);
  
  // Try each code block, prioritize 'json' tagged ones
  const jsonBlocks = allCodeBlocks.filter(m => m[1] === 'json' || m[1] === '');
  const otherBlocks = allCodeBlocks.filter(m => m[1] !== 'json' && m[1] !== '');
  const sortedBlocks = [...jsonBlocks, ...otherBlocks];
  
  for (const match of sortedBlocks) {
    const lang = match[1] || 'none';
    const codeContent = match[2].trim();
    log.debug(`Block [${lang}]: "${codeContent.substring(0, 80)}..."`);
    
    // Check if content looks like JSON
    if (codeContent.startsWith('[') || codeContent.startsWith('{')) {
      try {
        parsed = JSON.parse(codeContent);
        log.debug(`Parsed JSON from [${lang}] block`);
        break;
      } catch (e) {
        log.warn(`[${lang}] block looks like JSON but failed:`, e.message);
      }
    }
  }
  
  // If not parsed yet, try to extract JSON object or array from anywhere in response
  if (!parsed) {
    // Remove all code blocks first to avoid matching non-JSON code
    let cleanedResponse = response.replace(/```\w*[\s\S]*?```/g, '');
    
    // Also try finding JSON in the original response (in case it's outside blocks)
    const jsonMatch = cleanedResponse.match(/(\[[\s\S]*\]|\{[\s\S]*\})/) 
                   || response.match(/(\[[\s\S]*\]|\{[\s\S]*\})/);
    if (jsonMatch) {
      log.debug('Found JSON pattern outside code blocks, attempting parse...');
      try {
        parsed = JSON.parse(jsonMatch[0]);
        log.debug('Parsed JSON from pattern match');
      } catch (e) {
        log.warn('JSON pattern match failed:', e.message);
      }
    }
  }
  
  // Last resort: try parsing whole response
  if (!parsed) {
    log.debug('Trying to parse entire response as JSON...');
    try {
      parsed = JSON.parse(response.trim());
      log.debug('Parsed entire response as JSON');
    } catch (e) {
      log.error('Failed to parse LLM response as JSON');
      log.error('Response preview:', response.substring(0, 500));
      throw new Error(`LLM response is not valid JSON. Response starts with: "${response.substring(0, 100)}..."`);
    }
  }
  
  // Ensure we have an array of questions
  const questions = Array.isArray(parsed) ? parsed : [parsed];
  log.debug(`Extracted ${questions.length} question(s)`);
  
  // Normalize all questions to internal format
  return questions.map(normalizeQuestion);
}
